
import { Vue, Component, Watch } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';
import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css';
const namespace: string = 'cart';

@Component<CartPayWhatYouCan>({
	components: {
		VueSlider
	}
})
export default class CartPayWhatYouCan extends Vue {
	@Action('setChosenSubsidyDiscount', { namespace }) private setChosenSubsidyDiscount!: (chosenAmountToBePaid: number) => void;
	@Getter('getSubtotal', { namespace }) private subtotal!: string;
	@Getter('getSubsidyChosenAmountToBePaid', { namespace }) private subsidyChosenAmountToBePaid!: string | undefined;
	@Getter('getSubsidyDiscountAmount', { namespace }) private subsidyDiscountAmount!: string;
	@Watch('maxValue', { immediate: true })
	private onMaxValueChanged(newValue: number, oldValue?: number): void {
		if(newValue <= 0) {
			return;
		}

		// If this is the first time (initial setup)
		if (!this.initialValueSet) {
			// Check if there's already a subsidy amount set
			if (typeof this.subsidyChosenAmountToBePaid === 'string') {
				this.sliderValue = +this.subsidyChosenAmountToBePaid;
				this.setChosenSubsidyDiscount(this.sliderValue);
			}
			// Set to half of the max value if no subsidy amount is set
			else {
				this.sliderValue = newValue * 0.5;
				this.setChosenSubsidyDiscount(this.sliderValue);
				this.$emit('slider-value-updated', this.sliderValue);
			}
			this.initialValueSet = true;
		}
		// If max value decreased and current slider value exceeds the new max
		else if (oldValue !== undefined && newValue < oldValue && this.sliderValue > newValue) {
			this.sliderValue = newValue;
			this.setChosenSubsidyDiscount(this.sliderValue);
			this.$emit('slider-value-updated', this.sliderValue);
		}
	}

	private minValue: number = 0;
	private sliderValue: number = 0;
	private editValue: string = '0.00';
	private isEditing: boolean = false;
	private initialValueSet: boolean = false;
	private preventNextSliderChangeEvent: boolean = false;

	private get maxValue(): number {
		return +this.subtotal;
	}

	private get selectedValue(): string {
		return this.sliderValue.toFixed(2);
	}


	private onSliderDrag(value: number): void {
		this.sliderValue = value;
	}

	/**
	 * Handle the slider change event.
	 * Prevents the next change if `preventNextSliderChangeEvent` is true. This is to avoid a
	 * weird problem where the slider thinks the value has changed when it hasn't. But we still
	 * want the slider to listen to drag events even if the user is editing, thus we need to use this
	 * one time flag.
	 *
	 * @return {void}
	 */
	private onSliderChange(): void {
		if (this.preventNextSliderChangeEvent) {
			this.preventNextSliderChangeEvent = false;
			return;
		}
		this.setChosenSubsidyDiscount(this.sliderValue);
		this.$emit('slider-value-updated', this.sliderValue);
	}

	/**
	 * Starts editing the slider value by showing an input field.
	 *
	 * @return {void}
	 */
	private startEditing(): void {
		this.preventNextSliderChangeEvent = true;
		this.isEditing = true;
		this.editValue = this.sliderValue.toFixed(2);

		// Focus input
		this.$nextTick(() => {
			const input = this.$refs.tooltipInput as HTMLInputElement;
			if (input) {
				input.focus();
				input.select();
			}
		});
	}

	/**
	 * Saves the edited value from the input field,
	 * clamping it to min or max if out of range respectively.
	 *
	 * @return {void}
	 */
	private saveEdit(): void {
		const value = +this.editValue;
		this.preventNextSliderChangeEvent = false;

		if (isNaN(value)) {
			this.isEditing = false;
			return;
		}

		this.sliderValue = Math.min(Math.max(value, this.minValue), this.maxValue);
		this.onSliderChange();
		this.isEditing = false;
	}

	private cancelEdit(): void {
		this.preventNextSliderChangeEvent = false;
		this.isEditing = false;
		this.editValue = this.sliderValue.toFixed(2);
	}
}
